import staticData from "@/public/data/tour/results.json";
import Flex from "@/shared/components/common/Flex";
import Typography from "@/shared/components/common/Typography";
import { cn } from "@/shared/libs/commonUtils";
import { isUndefined } from "@/shared/libs/type-utils";
import type { TourAvailabilityItem } from "@/types/services/tour/apis/Availability";
import { Couch, Empty, MapTrifold, SealPercent } from "@phosphor-icons/react";
import dynamic from "next/dynamic";
import { useCallback, useMemo, useState } from "react";
import TourCardDiscount from "./TourCardBadge";
import TourCardCarousel from "./TourCardCarousel";
import TourCardPanel from "./TourCardPanel";
import TourCardPriceAndReserve from "./TourCardPriceAndReserve";
import TourCardSummary from "./TourCardSummary";
import TourCardToggle from "./TourCardToggle";
import {
  createUrlWithSearchParams,
  createSearchParams,
} from "@/shared/libs/url-utils";
import { usePathname } from "next/navigation";
import { useDeviceType } from "@/shared/context/DeviceContext";
import Link from "next/link";

const TourCardMap = dynamic(() => import("./TourCardMap"), {
  ssr: false,
});

const TourCardFacilities = dynamic(() => import("./TourCardFacilities"), {
  ssr: false,
});

const DEFAULT_PANEL_STATE: PanelsState = {
  map: "close",
  facilities: "close",
};

type ToggleState = "open" | "close";
export type PanelState = keyof PanelsState;
type PanelsState = {
  map: ToggleState;
  facilities: ToggleState;
};

interface TourCardProps {
  tour: TourAvailabilityItem;
  searchParams: SearchParams;
  searchId: string;
}

export default function TourCard({
  tour,
  searchParams,
  searchId,
}: TourCardProps) {
  const { discountLabel, locationLabel, bookedLabel, facilitiesLabel } =
    staticData.body.resultCard;
  const [togglePanel, setTogglePanel] =
    useState<PanelsState>(DEFAULT_PANEL_STATE);

  const {
    name,
    address,
    stars,
    images,
    mapLat,
    mapLong,
    total_price,
    slug,
    facilities,
    price,
    per_passenger_price,
    key,
    id,
  } = tour;

  const isPanelOpen = Object.values(togglePanel).includes("open");
  const isBooked = isUndefined(price) && isUndefined(total_price);

  const discountPercentage = useMemo(
    () =>
      !isUndefined(price) && !isUndefined(total_price) && total_price !== price
        ? Math.trunc(((total_price - price) / total_price) * 100)
        : 0,
    [price, total_price],
  );

  const handleTogglePanel = useCallback(
    (panel: PanelState) => () => {
      setTogglePanel((prev) => ({
        ...DEFAULT_PANEL_STATE,
        [panel]: prev[panel] === "open" ? "close" : "open",
      }));
    },
    [],
  );

  const params = [
    {
      name: "searchId",
      value: String(searchId),
    },
    {
      name: "accommodationId",
      value: String(id),
    },
    {
      name: "tourKey",
      value: String(key),
    },
  ];

  const NEXT_PAGE_URL = createUrlWithSearchParams(
    `${usePathname()}/detail/`,
    createSearchParams(new URLSearchParams(searchParams), params),
  );

  const TourCard = (
    <Flex
      direction={"col"}
      className="ring-1 ring-border rounded-lg bg-background shadow-1 md:shadow-0 hover:shadow-2 transition duration-75 ease-linear select-none relative"
    >
      <Flex
        className={cn(
          "w-full rounded-t-md md:rounded-t-lg p-3",
          isPanelOpen ? "ring-1 ring-border" : "",
        )}
        direction={{ default: "col", md: "row" }}
      >
        <Flex.Item
          className="w-full md:basis-3/12 relative"
          alignSelf={"center"}
        >
          {discountPercentage > 0 && (
            <TourCardDiscount>
              <SealPercent size={18} weight="fill" className="text-link" />
              <Flex gap={"1"}>
                <Typography
                  variant="caption"
                  fontWeight="semibold"
                  className="text-black"
                >
                  {discountPercentage}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight="normal"
                  className="text-black"
                >
                  {discountLabel}
                </Typography>
              </Flex>
            </TourCardDiscount>
          )}

          {isBooked && (
            <TourCardDiscount>
              <Empty size={18} weight="bold" className="text-destructive-80" />
              <Typography
                variant="caption"
                fontWeight="normal"
                className="text-black"
              >
                {bookedLabel}
              </Typography>
            </TourCardDiscount>
          )}

          <TourCardCarousel images={images} slug={tour.slug} />
        </Flex.Item>

        <Flex.Item className="md:basis-9/12 p-1 md:pr-4 max-md:pt-4">
          <Flex
            direction={"col"}
            justifyContent={"between"}
            className="h-full"
            gap={"3"}
          >
            <TourCardSummary name={name} address={address} stars={+stars} />

            <Flex
              justifyContent={"between"}
              alignItems={"end"}
              className="max-md:items-center max-md:justify-center"
            >
              <Flex.Item className="hidden md:flex">
                <Flex alignItems={"center"} gap={"1"}>
                  <Flex.Item className="hidden md:flex">
                    <TourCardToggle
                      handleToggleState={handleTogglePanel}
                      panel="map"
                      label={locationLabel}
                      icon="MapTrifold"
                      isOpen={togglePanel.map === "open"}
                    />
                  </Flex.Item>

                  <Flex.Item className="hidden md:flex">
                    <TourCardToggle
                      handleToggleState={handleTogglePanel}
                      label={facilitiesLabel}
                      panel="facilities"
                      icon="Couch"
                      isOpen={togglePanel.facilities === "open"}
                    />
                  </Flex.Item>
                </Flex>
              </Flex.Item>
              <TourCardPriceAndReserve
                tour={tour}
                searchParams={searchParams}
                slug={slug}
                searchId={searchId}
                nextPageUrl={NEXT_PAGE_URL}
              />
            </Flex>
          </Flex>
        </Flex.Item>
      </Flex>

      <TourCardPanel isOpen={isPanelOpen}>
        {togglePanel.map === "open" && (
          <TourCardMap mapLat={+mapLat} mapLong={+mapLong} />
        )}

        {togglePanel.facilities === "open" && (
          <TourCardFacilities facilities={facilities} />
        )}
      </TourCardPanel>
    </Flex>
  );

  return useDeviceType() === "mobile" ? (
    <Link href={NEXT_PAGE_URL}>{TourCard}</Link>
  ) : (
    TourCard
  );
}
